<!-- eslint-disable vue/attribute-hyphenation -->
<template>
  <div>
    <!-- eslint-disable-next-line vue/no-undef-components -->
    <zui-navigation
      :locale="locale"
      :activeItem="activeItem"
      :navItemsMap="navItemsMap"
      :workspaceOptions="workspaceOptions"
      :activeWorkspace="activeWorkspace"
      :translations="translations"
      :zendeskProductKeys="zendeskProductKeys"
      :helpMenuItems="helpMenuItems"
      :profileMenuItems="profileMenuItems"
      :subnavTitle="subnavTitle"
      :subnavItems="subnavItems"
      :subnavActionTooltip="subnavActionTooltip"
      :subnavActionMenuItems="subnavActionMenuItems"
      :hideToggleSubnavButton="hideToggleSubnavButton"
      :globalBanners="globalBanners"
      :globalProductBanners="globalProductBanners"
      :hasFlagManager="hasFlagManager"
    />

    <teleport v-if="isMounted" to="#vue-app-mount">
      <application />
    </teleport>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted } from 'vue'
import { useEventListener } from '@vueuse/core'
import { useQuery } from '@tanstack/vue-query'
import type {
  HelpMenuKey,
  NavigationKey,
  NavItem,
  WorkspaceOption,
  HelpMenuItem,
  SubnavItem,
  ProfileMenuKey,
  ProductKey,
  SubnavActionMenuItem,
  BannerItems,
  BannerActionKey,
  SearchResultType,
  ProductBannerKey,
} from '@klausapp/app-navigation/types'
import { ZUI_EVENTS } from '@klausapp/app-navigation/src/utils/event'

import { hasZendeskProxy } from '@/config'
import { getLocale } from '@/i18n'
import { getZendeskProducts } from '@/modules/shared/Navigation/components/ZendeskProductTray/api'
import Application from './Application.vue'

// Props
defineProps<{
  activeItem: string
  navItemsMap: NavItem[]
  workspaceOptions: WorkspaceOption[]
  activeWorkspace: WorkspaceOption
  translations: Record<string, string>
  helpMenuItems: HelpMenuItem[]
  subnavTitle: string
  subnavItems: SubnavItem[]
  subnavActionTooltip?: string
  profileMenuItems: any
  subnavActionMenuItems?: SubnavActionMenuItem[]
  hideToggleSubnavButton: boolean
  globalBanners: BannerItems[]
  globalProductBanners: ProductBannerKey[]
  hasFlagManager?: boolean
}>()

// Emits
const emit = defineEmits<{
  'nav-item-click': [key: NavigationKey]
  'workspace-click': [id: number]
  'help-menu-item-click': [key: HelpMenuKey | 'help']
  'profile-menu-click': [key: ProfileMenuKey | 'profile']
  'subnav-item-click': [navItem: string, subnavItem: string]
  'subnav-item-action-click': [navItem: string, subnavItem: string, action: string]
  'subnav-extra-click': [navItem: string, extraItem: string]
  'subnav-action-click': [key?: string]
  'banner-click': [id: string, action: BannerActionKey]
  'search-query-change': [query: string]
  'search-result-select': [type: SearchResultType, id: string, data?: unknown]
}>()

// Reactive state
const locale = getLocale()
const isMounted = ref(false)

const { data: zendeskProductKeys } = useQuery({
  queryKey: ['active-zendesk-products'],
  queryFn: getZendeskProducts,
  select: (data) => data.products,
  enabled: hasZendeskProxy,
  placeholderData: () => ({
    features: { product_tray_enable_list_view: true },
    products: [{ key: 'quality_assurance' as ProductKey }],
  }),
})

// Utility functions
const waitForElement = (selector: string, container: Element | ShadowRoot, timeout = 5000): Promise<Element> => {
  return new Promise((resolve, reject) => {
    const startTime = Date.now()

    const tryToFind = (): void => {
      const element = container.querySelector(selector)

      if (element) {
        return resolve(element)
      }

      if (Date.now() - startTime > timeout) {
        return reject(new Error(`[ZUI] Element "${selector}" not found after ${timeout}ms`))
      }

      requestAnimationFrame(tryToFind)
    }

    tryToFind()
  })
}

const setupEventListeners = (element: HTMLElement | Element): void => {
  if (element) {
    useEventListener(element, ZUI_EVENTS.WORKSPACE_CLICK, (event: CustomEvent) => {
      emit(ZUI_EVENTS.WORKSPACE_CLICK, event.detail.id)
    })

    useEventListener(element, ZUI_EVENTS.HELP_MENU_ITEM_CLICK, (event: CustomEvent) => {
      emit(ZUI_EVENTS.HELP_MENU_ITEM_CLICK, event.detail.key)
    })

    useEventListener(element, ZUI_EVENTS.PROFILE_MENU_CLICK, (event: CustomEvent) => {
      emit(ZUI_EVENTS.PROFILE_MENU_CLICK, event.detail.key)
    })

    useEventListener(element, ZUI_EVENTS.NAV_ITEM_CLICK, (event: CustomEvent) => {
      emit(ZUI_EVENTS.NAV_ITEM_CLICK, event.detail.key)
    })

    useEventListener(element, ZUI_EVENTS.SUBNAV_ITEM_CLICK, (event: CustomEvent) => {
      emit(ZUI_EVENTS.SUBNAV_ITEM_CLICK, event.detail.navItem, event.detail.subnavItem)
    })

    useEventListener(element, ZUI_EVENTS.SUBNAV_ITEM_ACTION_CLICK, (event: CustomEvent) => {
      emit(ZUI_EVENTS.SUBNAV_ITEM_ACTION_CLICK, event.detail.navItem, event.detail.subnavItem, event.detail.action)
    })

    useEventListener(element, ZUI_EVENTS.SUBNAV_EXTRA_CLICK, (event: CustomEvent) => {
      emit(ZUI_EVENTS.SUBNAV_EXTRA_CLICK, event.detail.navItem, event.detail.extraItem)
    })

    useEventListener(element, ZUI_EVENTS.SUBNAV_ACTION_CLICK, (event: CustomEvent) => {
      emit(ZUI_EVENTS.SUBNAV_ACTION_CLICK, event.detail.key)
    })

    useEventListener(element, ZUI_EVENTS.BANNER_CLICK, (event: CustomEvent) => {
      emit(ZUI_EVENTS.BANNER_CLICK, event.detail.id, event.detail.action)
    })

    // Search events
    useEventListener(element, ZUI_EVENTS.SEARCH_QUERY_CHANGE, (event: CustomEvent) => {
      emit(ZUI_EVENTS.SEARCH_QUERY_CHANGE, event.detail.query)
    })

    useEventListener(element, ZUI_EVENTS.SEARCH_RESULT_SELECT, (event: CustomEvent) => {
      emit(ZUI_EVENTS.SEARCH_RESULT_SELECT, event.detail.type, event.detail.id, event.detail.data)
    })
  }
}

const initializeNavigation = async (): Promise<void> => {
  try {
    // Wait for custom element to be defined
    await customElements.whenDefined('zui-navigation')

    const zuiNavigationHost = document.querySelector('zui-navigation')

    if (!zuiNavigationHost) {
      throw new Error('[ZUI] Could not find zui-navigation element')
    }

    // Setup event listeners
    setupEventListeners(zuiNavigationHost)

    // Wait for the mount point to be ready
    await waitForElement('#vue-app-mount', zuiNavigationHost)

    // Enable Teleport to render Application
    isMounted.value = true
  } catch (error) {
    console.error('[ZUI] Error during navigation initialization:', error)
  }
}

// Lifecycle hooks
onMounted(() => {
  initializeNavigation()
})
</script>
